import { useEffect, useState } from 'react'
import { articles as seedArticles } from '@/content/articles'
import { getCmsArticles } from '@/lib/cmsStore'
import { subscribeCmsChange } from '@/lib/cmsEvents'
import { bundledArticleToBlocks } from '@/lib/bundledArticleToBlocks'
import type { Article, BundledArticle } from '@/types/article'

function bundledToRuntime(b: BundledArticle): Article {
  return {
    slug: b.slug,
    title: b.title,
    date: b.date,
    tags: b.tags,
    excerpt: b.excerpt,
    readTime: b.readTime,
    featured: b.featured,
    body: bundledArticleToBlocks(b),
    coverImage: b.coverImage ?? null,
  }
}

function mergeArticles(): Article[] {
  const cms = getCmsArticles()
  const cmsSlugs = new Set(cms.map(a => a.slug))
  const seedsWithoutOverrides = seedArticles
    .filter(a => !cmsSlugs.has(a.slug))
    .map(bundledToRuntime)
  return [...cms, ...seedsWithoutOverrides]
}

export function getArticles(): Article[] {
  return mergeArticles().sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
}

// The `articles` parameter on the lookup helpers below lets a React caller
// pass the list it already holds from useArticles(), so one render reads the
// store once instead of once per helper. Omitting it keeps the original
// non-React behaviour (read the store on every call).
export function getArticleBySlug(slug: string, articles: Article[] = mergeArticles()): Article | undefined {
  return articles.find(a => a.slug === slug)
}

export function getFeaturedArticle(): Article | undefined {
  return mergeArticles().find(a => a.featured)
}

export function getArticlesByTag(tag: string): Article[] {
  return getArticles().filter(a => a.tags.includes(tag))
}

export function getLatestArticles(count: number): Article[] {
  return getArticles().slice(0, count)
}

export function getRelatedArticles(slug: string, count = 2, articles: Article[] = getArticles()): Article[] {
  const current = getArticleBySlug(slug, articles)
  if (!current) return []
  return articles
    .filter(a => a.slug !== slug && a.tags.some(t => current.tags.includes(t)))
    .slice(0, count)
}

// Chronological neighbours in the newest-first article list.
//   - `prev` = the older article (next index, since list is desc by date)
//   - `next` = the newer article (previous index)
// Returns `null` on either side when the current article is the first or last.
export function getAdjacentArticles(
  slug: string,
  articles: Article[] = getArticles(),
): { prev: Article | null; next: Article | null } {
  const idx = articles.findIndex(a => a.slug === slug)
  if (idx < 0) return { prev: null, next: null }
  return {
    next: idx > 0 ? articles[idx - 1] : null,
    prev: idx < articles.length - 1 ? articles[idx + 1] : null,
  }
}

/**
 * Reactive read of the merged seed + CMS article list.
 *
 * getArticles() is a synchronous snapshot of localStorage, so a component
 * that calls it bare (or memoises it with `[]` deps) freezes the list at
 * mount — articles published from /admin only surfaced after a full remount.
 * Subscribing to the CMS bus closes that: cmsCollection* writes the
 * `ck_cache_coll_articles` cache *before* emitting, so re-reading the store
 * inside the listener already sees the optimistic row.
 *
 * Mirrors the subscribe/filter shape of useResource + useConfig in
 * lib/siteData.ts. The returned array is referentially stable between
 * renders, so callers can safely memoise derived lists on `[articles]`.
 */
export function useArticles(): Article[] {
  const [articles, setArticles] = useState<Article[]>(() => getArticles())

  useEffect(() => {
    return subscribeCmsChange(scope => {
      if (scope === 'articles' || scope === 'all') setArticles(getArticles())
    })
  }, [])

  return articles
}
